import React, { useState, useEffect } from 'react';
import { YamlFormatter } from '../../lib/formatters/yamlFormatter';
import { YamlParser } from '../../lib/parsers/yamlParser';
import './JsonYamlConverter.css';

export const JsonYamlConverter: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [yamlInput, setYamlInput] = useState('');
  const [jsonError, setJsonError] = useState<string>('');
  const [yamlError, setYamlError] = useState<string>('');
  const [indentation, setIndentation] = useState(2);
  const [conversionStatus, setConversionStatus] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Validate JSON input in real-time
  useEffect(() => {
    if (jsonInput.trim()) {
      try {
        JSON.parse(jsonInput);
        setJsonError('');
      } catch (error) {
        setJsonError('Invalid JSON format');
      }
    } else {
      setJsonError('');
    }
  }, [jsonInput]);

  // Validate YAML input in real-time
  useEffect(() => {
    if (yamlInput.trim()) {
      if (!YamlParser.validateYaml(yamlInput)) {
        setYamlError('Invalid YAML format');
      } else {
        setYamlError('');
      }
    } else {
      setYamlError('');
    }
  }, [yamlInput]);

  const handleJsonToYaml = async () => {
    if (!jsonInput.trim()) {
      setConversionStatus('Please enter JSON data');
      return;
    }

    if (jsonError) {
      setConversionStatus('Please fix JSON errors before converting');
      return;
    }

    setIsProcessing(true);
    setConversionStatus('');

    try {
      const yamlResult = YamlFormatter.jsonToYaml(JSON.parse(jsonInput), indentation);
      setYamlInput(yamlResult);
      setConversionStatus('JSON → YAML conversion successful');
      setYamlError('');
    } catch (error) {
      setConversionStatus('Conversion failed');
      setYamlInput('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleYamlToJson = async () => {
    if (!yamlInput.trim()) {
      setConversionStatus('Please enter YAML data');
      return;
    }

    if (yamlError) {
      setConversionStatus('Please fix YAML errors before converting');
      return;
    }

    setIsProcessing(true);
    setConversionStatus('');

    try {
      const jsonResult = YamlParser.toJson(yamlInput);
      setJsonInput(jsonResult);
      setConversionStatus('YAML → JSON conversion successful');
      setJsonError('');
    } catch (error) {
      setConversionStatus('Conversion failed');
      setJsonInput('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyJson = async () => {
    if (jsonInput) {
      try {
        await navigator.clipboard.writeText(jsonInput);
        setConversionStatus('JSON copied to clipboard');
      } catch (error) {
        setConversionStatus('Failed to copy JSON');
      }
    }
  };

  const handleCopyYaml = async () => {
    if (yamlInput) {
      try {
        await navigator.clipboard.writeText(yamlInput);
        setConversionStatus('YAML copied to clipboard');
      } catch (error) {
        setConversionStatus('Failed to copy YAML');
      }
    }
  };

  const handleCopyAll = async () => {
    const allContent = `JSON:\n${jsonInput}\n\nYAML:\n${yamlInput}`;
    try {
      await navigator.clipboard.writeText(allContent);
      setConversionStatus('All data copied to clipboard');
    } catch (error) {
      setConversionStatus('Failed to copy data');
    }
  };

  const handleClear = () => {
    setJsonInput('');
    setYamlInput('');
    setJsonError('');
    setYamlError('');
    setConversionStatus('');
  };

  const handlePresetClick = (preset: string) => {
    setJsonInput(preset);
    setYamlInput('');
    setConversionStatus('');
  };

  const formatOptions = YamlFormatter.getFormattingOptions();

  return (
    <div className="json-yaml-converter" role="main">
      <h1>JSON ↔ YAML Converter</h1>
      <p>Seamlessly convert between JSON and YAML formats with data integrity.</p>

      <div className="input-section">
        <div className="input-row">
          <div className="input-column">
            <label htmlFor="json-input">
              JSON Input
              {jsonError && <span className="error-indicator">❌</span>}
            </label>
            <textarea
              id="json-input"
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="Enter JSON data..."
              className={jsonError ? 'error' : ''}
              aria-describedby="json-help json-error"
              data-testid="json-input"
              rows={12}
            />
            <small id="json-help" className="help-text">
              Enter valid JSON to convert to YAML format
            </small>
            {jsonError && (
              <div id="json-error" className="error-message" role="alert">
                {jsonError}
              </div>
            )}
          </div>

          <div className="input-column">
            <label htmlFor="yaml-input">
              YAML Input
              {yamlError && <span className="error-indicator">❌</span>}
            </label>
            <textarea
              id="yaml-input"
              value={yamlInput}
              onChange={(e) => setYamlInput(e.target.value)}
              placeholder="Enter YAML data..."
              className={yamlError ? 'error' : ''}
              aria-describedby="yaml-help yaml-error"
              data-testid="yaml-input"
              rows={12}
            />
            <small id="yaml-help" className="help-text">
              Enter valid YAML to convert to JSON format
            </small>
            {yamlError && (
              <div id="yaml-error" className="error-message" role="alert">
                {yamlError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="options-section">
        <div className="option-group">
          <label htmlFor="indentation">Indentation:</label>
          <select
            id="indentation"
            value={indentation}
            onChange={(e) => setIndentation(Number(e.target.value))}
            data-testid="indentation-select"
          >
            {formatOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="button-section">
        <button
          onClick={handleJsonToYaml}
          disabled={!jsonInput.trim() || !!jsonError || isProcessing}
          className="convert-button convert-to-yaml"
          data-testid="convert-to-yaml"
        >
          JSON → YAML
        </button>

        <button
          onClick={handleYamlToJson}
          disabled={!yamlInput.trim() || !!yamlError || isProcessing}
          className="convert-button convert-to-json"
          data-testid="convert-to-json"
        >
          YAML → JSON
        </button>
      </div>

      <div className="presets-section">
        <h3>JSON Presets</h3>
        <div className="presets">
          {YamlFormatter.getPresetExamples().map((preset, index) => (
            <button
              key={index}
              onClick={() => handlePresetClick(preset.json)}
              className="preset-button"
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {conversionStatus && (
        <div
          className={`status-message ${conversionStatus.includes('failed') || conversionStatus.includes('Please fix') ? 'error' : 'success'}`}
          role="status"
          aria-live="polite"
        >
          {conversionStatus}
        </div>
      )}

      {(jsonInput || yamlInput) && (
        <div className="actions">
          <button
            onClick={handleCopyJson}
            disabled={!jsonInput}
            className="action-button"
            aria-label="Copy JSON to clipboard"
            data-testid="copy-json"
          >
            Copy JSON
          </button>

          <button
            onClick={handleCopyYaml}
            disabled={!yamlInput}
            className="action-button"
            aria-label="Copy YAML to clipboard"
            data-testid="copy-yaml"
          >
            Copy YAML
          </button>

          <button
            onClick={handleCopyAll}
            disabled={!jsonInput && !yamlInput}
            className="action-button"
            aria-label="Copy all data to clipboard"
            data-testid="copy-all"
          >
            Copy All
          </button>

          <button
            onClick={handleClear}
            className="action-button clear-button"
            aria-label="Clear all inputs"
            data-testid="clear-all"
          >
            Clear All
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="processing-indicator">
          <span>Processing...</span>
        </div>
      )}
    </div>
  );
};